/**
 * Actuals Entry Form
 * Monthly actuals entry for performance tracking
 */
import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ActualsEntryForm = ({ planId, organizationId }) => {
  const [actuals, setActuals] = useState({
    fiscal_year: new Date().getFullYear(),
    period_type: 'monthly',
    period_start: '',
    period_end: '',
    revenue_actual: '',
    expense_actual: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const saveActuals = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/budget-actuals`, {
        organization_id: organizationId || 'default',
        ...actuals
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Actuals saved successfully!');
      setActuals({
        ...actuals,
        revenue_actual: '',
        expense_actual: '',
        notes: ''
      });
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const profit = (parseFloat(actuals.revenue_actual) || 0) - (parseFloat(actuals.expense_actual) || 0);

  return (
    <div className="max-w-4xl mx-auto p-8">
      <h2 className="text-3xl font-bold mb-2">Enter Monthly Actuals</h2>
      <p className="text-gray-600 mb-6">Record actual performance for variance analysis</p>

      <form onSubmit={saveActuals} className="bg-white rounded-lg shadow-lg p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium mb-2">Period Type</label>
            <select
              value={actuals.period_type}
              onChange={(e) => setActuals({...actuals, period_type: e.target.value})}
              className="w-full border rounded-lg px-4 py-2"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Fiscal Year</label>
            <input
              type="number"
              value={actuals.fiscal_year}
              onChange={(e) => setActuals({...actuals, fiscal_year: parseInt(e.target.value)})}
              className="w-full border rounded-lg px-4 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Period Start Date *</label>
            <input
              type="date"
              value={actuals.period_start}
              onChange={(e) => setActuals({...actuals, period_start: e.target.value})}
              className="w-full border rounded-lg px-4 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Period End Date *</label>
            <input
              type="date"
              value={actuals.period_end}
              onChange={(e) => setActuals({...actuals, period_end: e.target.value})}
              className="w-full border rounded-lg px-4 py-2"
              required
            />
          </div>
        </div>

        <div className="border-t pt-6 mb-6">
          <h3 className="font-bold text-lg mb-4">Financial Data</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Revenue (Actual) *</label>
              <input
                type="number"
                step="0.01"
                value={actuals.revenue_actual}
                onChange={(e) => setActuals({...actuals, revenue_actual: e.target.value})}
                placeholder="e.g., 1500000"
                className="w-full border rounded-lg px-4 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Expenses (Actual) *</label>
              <input
                type="number"
                step="0.01"
                value={actuals.expense_actual}
                onChange={(e) => setActuals({...actuals, expense_actual: e.target.value})}
                placeholder="e.g., 1200000"
                className="w-full border rounded-lg px-4 py-2"
                required
              />
            </div>
          </div>
          
          {/* Auto-calculated Profit */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium">Profit (Auto-calculated):</span>
              <span className={`text-2xl font-bold ${
                profit >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                ETB {profit.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
          <textarea
            value={actuals.notes}
            onChange={(e) => setActuals({...actuals, notes: e.target.value})}
            placeholder="Any explanations for variances or special circumstances..."
            className="w-full border rounded-lg px-4 py-2 h-24"
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-medium disabled:bg-gray-300"
        >
          {saving ? '💾 Saving...' : '💾 Save Actuals'}
        </button>
      </form>
    </div>
  );
};

export default ActualsEntryForm;